export type ThreadSyncReplicaSourceKind =
  | "foreground_ws"
  | "foreground_http_prefill"
  | "background_http"
  | "hydrated"

export interface ThreadSyncReplicaProvenance {
  sourceKind: ThreadSyncReplicaSourceKind
  sourceTabId: string | null
}

function normalizeNonEmptyString(value: unknown): string | null {
  if (typeof value !== "string") {
    return null
  }

  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

export function normalizeThreadSyncReplicaSourceKind(
  value: unknown,
): ThreadSyncReplicaSourceKind | null {
  if (
    value === "foreground_ws" ||
    value === "foreground_http_prefill" ||
    value === "background_http" ||
    value === "hydrated"
  ) {
    return value
  }

  return null
}

export function buildHydratedThreadSyncReplicaProvenance(): ThreadSyncReplicaProvenance {
  return {
    sourceKind: "hydrated",
    sourceTabId: null,
  }
}

export function buildThreadSyncReplicaProvenance(args: {
  sourceKind: ThreadSyncReplicaSourceKind
  sourceTabId?: string | null
}): ThreadSyncReplicaProvenance {
  return {
    sourceKind: args.sourceKind,
    sourceTabId: normalizeNonEmptyString(args.sourceTabId) ?? null,
  }
}

export function isTrustedThreadSyncReplicaResumeProvenance(
  provenance: Pick<ThreadSyncReplicaProvenance, "sourceKind"> | null | undefined,
): boolean {
  // Foreground websocket, foreground HTTP prefill, and background HTTP warming
  // all consume the same SessionDO resume contract and only persist resumable
  // metadata when the local snapshot/raw projection line up with a validated
  // cursor + history token. Treat those persisted replicas as safe resumable
  // inputs. Legacy/unknown hydrated rows still stay render-only.
  return (
    provenance?.sourceKind === "foreground_ws"
    || provenance?.sourceKind === "foreground_http_prefill"
    || provenance?.sourceKind === "background_http"
  )
}

export function parseThreadSyncReplicaProvenance(
  value: unknown,
): ThreadSyncReplicaProvenance {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return buildHydratedThreadSyncReplicaProvenance()
  }

  const raw = value as Record<string, unknown>
  return {
    sourceKind:
      normalizeThreadSyncReplicaSourceKind(raw.sourceKind) ??
      buildHydratedThreadSyncReplicaProvenance().sourceKind,
    sourceTabId: normalizeNonEmptyString(raw.sourceTabId) ?? null,
  }
}
